import { ClientType } from '../../../api';
import { FormState } from '../../../store/ClientOnboardingFormAtom';
import { includesMpm } from '../../../utils/features';

export interface WizardStepConfig {
    key: string;
    label: string;
    /** One-line section description shown in the single-page section head. */
    description: string;
    fieldsToValidate: (formValues: FormState) => string[];
    isVisible?: (formValues: FormState) => boolean;
}

const isPersonType = (clientType: FormState['clientType']) =>
    clientType === ClientType.Individual || clientType === ClientType.Staff;

/**
 * Single-page sections (also drive the optional stepper). The old
 * "Systems & Type" + "General Info" steps are merged into "Profile";
 * "Projects" is conditional on the ModernPM platform toggle.
 */
export const wizardSteps: WizardStepConfig[] = [
    {
        key: 'profile',
        label: 'Profile',
        description: 'Which systems to create in, the client type, and who they are.',
        fieldsToValidate: (formValues) => {
            const fields = ['clientType', 'generalSection.officeId', 'generalSection.businessUnitId'];
            if (isPersonType(formValues.clientType)) {
                fields.push('generalSection.individualSection.firstName', 'generalSection.individualSection.lastName');
            } else {
                fields.push('generalSection.businessSection.businessName');
            }
            return fields;
        },
    },
    {
        key: 'contact',
        label: 'Contact',
        description: 'Address, phone, and email details.',
        fieldsToValidate: () => [
            'contactSection.mainAddress.streetLine',
            'contactSection.mainAddress.city',
            'contactSection.mainAddress.state',
            'contactSection.mainAddress.zip',
            'contactSection.mainPhone',
            'contactSection.mainEmail',
        ],
    },
    {
        key: 'admin',
        label: 'Admin',
        description: 'Engagement team, year end, and filing details.',
        fieldsToValidate: (formValues) => {
            const isStaff = formValues.clientType === ClientType.Staff;
            const fields = ['adminSection.yearEnd'];
            if (!isStaff) {
                fields.push(
                    'adminSection.lineOfBusinessId',
                    'adminSection.primaryPartnerId',
                    'adminSection.billerId',
                    'adminSection.rainmakerId'
                );
            }
            return fields;
        },
    },
    {
        key: 'projects',
        label: 'Projects',
        description:
            'Each project becomes an engagement for this client in ModernPM, built from a template. Pick a template, ' +
            'fill in the details and select Add Project; repeat to add as many projects as the client needs. ' +
            "The first project's owner becomes the client owner in ModernPM.",
        fieldsToValidate: () => [],
        // Hidden entirely when ModernPM creation is disabled (VITE_ENABLE_MPM), regardless of form state.
        isVisible: (formValues) => includesMpm(formValues.systems),
    },
];
